import { useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Layers,
  Database,
  Gamepad2,
  TrafficCone,
  User,
  Settings,
  ShieldCheck,
  PanelLeftClose,
  PanelLeftOpen,
  LogOut,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/useAuth";
import { useOutsideClick } from "@/hooks/useOutsideClick";
import { usePendingApprovals } from "@/pages/adminDashboard/hooks/usePendingApprovals";
import { getUserInitials } from "@/utils/userUtils";
import mdfMark from "@/assets/mdf-mark.png";

/** Shown next to a collapsed item (the rail shows icons only). */
const Tooltip = ({ text }: { text: string }) => (
  <span
    role="tooltip"
    className="pointer-events-none absolute top-1/2 left-full z-50 ml-2 -translate-y-1/2 rounded-full bg-popover px-3 py-1 text-xs font-medium whitespace-nowrap text-popover-foreground opacity-0 shadow-sm ring-1 ring-foreground/10 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
  >
    {text}
  </span>
);

interface Item {
  to: string;
  label: string;
  icon: LucideIcon;
  visible: boolean;
  /** A red count on the item (accounts waiting for approval); 0 shows nothing. */
  badge?: number;
}

/** "9+" keeps the bubble small. */
const badgeText = (count: number) => (count > 9 ? "9+" : String(count));

/**
 * The app's navigation: a rail on the left (desktop), in the mdf.lu style.
 * The MDF mark on top, the user's pages in the middle, settings and the user
 * menu at the bottom. Collapsed it shows icons with tooltips.
 */
const Sidebar = () => {
  const { t } = useTranslation("header");
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { isPlatformAdmin, hasModule, me, logout } = useAuth();
  const waiting = usePendingApprovals();
  useOutsideClick(menuRef, () => setShowMenu(false));

  const displayName = me?.name || me?.email?.split("@")[0] || t("header.user");

  // Pages come from the user's access groups (/v1/me); the admin panel is
  // for platform admins only.
  const mainItems: Item[] = [
    { to: "/digitaltwin", label: t("sidebar.digitaltwin"), icon: Layers, visible: hasModule("digital-twin") },
    { to: "/digitaltwin/quiz", label: "Road Quiz", icon: Gamepad2, visible: hasModule("quiz") },
    { to: "/digitaltwin/scenarios", label: "Scenarios", icon: TrafficCone, visible: hasModule("scenarios") },
    { to: "/analyzeStation", label: t("sidebar.analyzeStation"), icon: Database, visible: hasModule("analyze-stations") },
    {
      // With accounts waiting, the admin panel opens on them.
      to: waiting > 0 ? "/admin?show=waiting" : "/admin",
      label: t("sidebar.adminPanel"),
      icon: ShieldCheck,
      visible: isPlatformAdmin,
      badge: waiting,
    },
  ];

  const itemClass = (isActive: boolean) =>
    cn(
      "group relative flex h-10 w-full items-center gap-3 rounded-full px-3 text-sm font-medium transition-colors",
      isActive
        ? "bg-secondary text-foreground [&_svg]:text-primary"
        : "text-muted-foreground hover:bg-muted hover:text-foreground",
      !isOpen && "justify-center px-0",
    );

  const renderItem = ({ to, label, icon: Icon, badge = 0 }: Item) => {
    const note = badge > 0 ? `${label} · ${badge} waiting for approval` : label;
    return (
      <NavLink
        key={to}
        to={to}
        end={to === "/digitaltwin"}
        aria-label={note}
        className={({ isActive }) => itemClass(isActive)}
      >
        <span className="relative shrink-0">
          <Icon className="size-[18px]" />
          {badge > 0 && !isOpen && (
            <span className="absolute -top-1.5 -right-2 grid h-4 min-w-4 place-items-center rounded-full bg-status-critical px-1 text-[10px] leading-none font-semibold text-white">
              {badgeText(badge)}
            </span>
          )}
        </span>
        {isOpen ? (
          <>
            <span className="truncate">{label}</span>
            {badge > 0 && (
              <span className="ml-auto grid h-5 min-w-5 place-items-center rounded-full bg-status-critical px-1.5 text-[11px] leading-none font-semibold text-white">
                {badgeText(badge)}
              </span>
            )}
          </>
        ) : (
          <Tooltip text={note} />
        )}
      </NavLink>
    );
  };

  return (
    <div
      className={cn(
        "flex h-full flex-col border-r border-sidebar-border bg-sidebar transition-[width] duration-300 ease-in-out",
        isOpen ? "w-56" : "w-16",
      )}
    >
      {/* The MDF mark: back to the start page */}
      <button
        type="button"
        onClick={() => navigate("/")}
        aria-label={t("header.title")}
        className={cn(
          "group relative flex h-16 shrink-0 cursor-pointer items-center gap-2 border-none bg-transparent px-3",
          !isOpen && "justify-center px-0",
        )}
      >
        <img src={mdfMark} alt="" className="size-9 shrink-0 object-contain brightness-0 dark:invert" />
        {isOpen ? (
          <span className="text-left leading-none font-heading">
            <span className="block text-[19px] font-bold tracking-[-0.04em] text-foreground">MDF</span>
            <span className="mt-0.5 block text-[10px] font-medium text-muted-foreground">{t("header.title")}</span>
          </span>
        ) : (
          <Tooltip text={t("header.title")} />
        )}
      </button>

      {/* The user's pages */}
      {/* No overflow container: it would clip the tooltips that open to the right. */}
      <nav className="flex-1 space-y-1 px-2.5 py-2">
        {mainItems.filter((i) => i.visible).map(renderItem)}
      </nav>

      {/* Settings, the user menu and the collapse toggle */}
      <div className="space-y-1 border-t border-sidebar-border px-2.5 py-3">
        {renderItem({ to: "/settings", label: t("sidebar.settings"), icon: Settings, visible: true })}

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setShowMenu((v) => !v)}
            aria-label={displayName}
            aria-expanded={showMenu}
            className={cn(
              "group relative flex h-10 w-full cursor-pointer items-center gap-2.5 rounded-full border-none bg-transparent px-1.5 text-left transition-colors hover:bg-muted",
              showMenu && "bg-muted",
              !isOpen && "justify-center px-0",
            )}
          >
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary text-[11px] font-semibold text-primary-foreground">
              {getUserInitials(displayName)}
            </span>
            {isOpen ? (
              <span className="min-w-0">
                <span className="block truncate text-xs font-semibold text-foreground">{displayName}</span>
                {me?.email && <span className="block truncate text-[11px] text-muted-foreground">{me.email}</span>}
              </span>
            ) : (
              !showMenu && <Tooltip text={displayName} />
            )}
          </button>

          {showMenu && (
            <div className="absolute bottom-0 left-full z-1001 ml-2 w-56 overflow-hidden rounded-2xl border bg-popover p-1 text-popover-foreground shadow-[0_24px_60px_rgb(11_18_32/0.14)]">
              <div className="border-b px-3 py-2.5">
                <p className="truncate text-xs font-semibold">{displayName}</p>
                {me?.email && <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{me.email}</p>}
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  navigate("/profile");
                }}
                className="mt-1 flex w-full cursor-pointer items-center gap-2.5 rounded-xl border-none bg-transparent px-2.5 py-2 text-xs text-foreground transition-colors hover:bg-muted"
              >
                <User className="size-3.5 shrink-0 text-muted-foreground" />
                {t("header.profile")}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  void logout();
                }}
                className="flex w-full cursor-pointer items-center gap-2.5 rounded-xl border-none bg-transparent px-2.5 py-2 text-xs text-status-critical-strong transition-colors hover:bg-status-critical-subtle"
              >
                <LogOut className="size-3.5 shrink-0" />
                {t("header.signOut")}
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          aria-label={isOpen ? "Collapse sidebar" : "Expand sidebar"}
          className={cn(
            "group relative flex h-9 w-full cursor-pointer items-center gap-3 rounded-full border-none bg-transparent px-3 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
            !isOpen && "justify-center px-0",
          )}
        >
          {isOpen ? <PanelLeftClose className="size-4 shrink-0" /> : <PanelLeftOpen className="size-4 shrink-0" />}
          {isOpen ? <span>Collapse</span> : <Tooltip text="Expand" />}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
